import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type {
  MessageTemplate,
  PlaceholderCatalogueEntry,
  TemplateIssue,
  TemplatePreview,
} from '../types/domain';

const TEMPLATES_KEY = ['masters', 'templates'] as const;

export interface TemplatesResponse {
  templates: MessageTemplate[];
  catalogue: PlaceholderCatalogueEntry[];
  deliveryFormats: Record<string, 'Attachment' | 'Link'>;
}

export function useTemplatesList() {
  return useQuery({
    queryKey: [...TEMPLATES_KEY, 'list'],
    queryFn: () => api.get<TemplatesResponse>('/api/v1/masters/templates'),
  });
}

export interface TemplateWriteResult {
  template: MessageTemplate;
  warnings: TemplateIssue[];
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; subject?: string; bodyHtml?: string; isActive?: boolean; reason?: string }) =>
      api.put<TemplateWriteResult>(`/api/v1/masters/templates/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_KEY }),
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      templateKey: string;
      language: string;
      /** null / omitted = all markets; 'CH' = Switzerland. */
      marketCode?: string | null;
      subject: string;
      bodyHtml: string;
      isActive?: boolean;
      reason?: string;
    }) =>
      api.post<TemplateWriteResult>('/api/v1/masters/templates', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_KEY }),
  });
}

/**
 * The preview is a mutation rather than a query on purpose: it renders text the
 * author has NOT saved, so there is no key to cache it under and no moment when
 * refetching it would be correct.
 */
export function usePreviewTemplate() {
  return useMutation({
    mutationFn: (body: {
      subject: string;
      bodyHtml: string;
      draftType: string;
      docTypeCode: string | null;
      isActive: boolean;
      /**
       * The template's language. The server previews `{{salutation}}` and
       * `{{requestSummary}}` in it, because those two are the ones the model
       * writes in the reply language — a German template previewed with an
       * English greeting is the reason this field exists.
       */
      language?: string;
    }) =>
      api.post<TemplatePreview>('/api/v1/masters/templates/preview', body),
  });
}
